import { useState } from 'react';

import { GlassCard } from '../../GlassCard/GlassCard';
import type { AdvisorDashboardFocusMessage, GlassCardSpan, GlassCardVariant, TodaysFocusCta } from '../../types';

import './_todaysFocusCard.scss';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import Carousel from 'nuka-carousel';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface TodaysFocusCardDotsProps {
  count: number;
  currentIndex: number;
  onSelect: (index: number) => void;
}

function TodaysFocusCardDots({ count, currentIndex, onSelect }: TodaysFocusCardDotsProps) {
  const slideNavAria = (i18n.translate('glassWidgets.todaysfocus.slideNavAria') as string) || 'Slide navigation';
  const slideAriaBase = (i18n.translate('glassWidgets.todaysfocus.slideAria') as string) || 'Slide';
  return (
    <div className="todays-focus-card__dots" role="tablist" aria-label={slideNavAria}>
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={currentIndex === index}
          aria-label={`${slideAriaBase} ${index + 1}`}
          className={clsx('todays-focus-card__dot', {
            'todays-focus-card__dot--active': index === currentIndex,
          })}
          onClick={() => {
            onSelect(index);
          }}
        />
      ))}
    </div>
  );
}

/** Renders the slide call to action (CTA) control (link or anchor). */
function renderSlideCta(cta: TodaysFocusCta, index: number) {
  const { label, url, external, newTab, openInFancybox, icon } = cta;
  const iconEl = icon ? (
    <FontAwesomeIcon icon={['fas', icon as IconName]} className="todays-focus-card__link-btn-icon" />
  ) : null;

  if (openInFancybox) {
    return (
      <a key={index} className="todays-focus-card__link-btn" data-fancybox data-type="iframe" href={url}>
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }
  if (external) {
    return (
      <a
        key={index}
        className="todays-focus-card__link-btn"
        href={url}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
      >
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }
  if (newTab) {
    const base = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname || ''}` : '';
    const hashPath = (url ?? '').startsWith('/') ? url : `/${url}`;
    return (
      <a
        key={index}
        className="todays-focus-card__link-btn"
        href={`${base}#${hashPath}`}
        target="_blank"
        rel="noreferrer"
      >
        {iconEl}
        <span>{label}</span>
      </a>
    );
  }
  return (
    <Link key={index} to={url} className="todays-focus-card__link-btn">
      {iconEl}
      <span>{label}</span>
    </Link>
  );
}

/** Builds a `TodaysFocusCta` from API call to action (CTA) fields (`cta_label`, `cta_url`). */
function focusMessageToCta(m: AdvisorDashboardFocusMessage): TodaysFocusCta | null {
  const url = m.cta_url;
  if (!m.cta_label || !url) return null;
  const external = /^https?:\/\//i.test(url);
  return { label: m.cta_label, url, external: external || undefined, newTab: external || undefined };
}

/** Wraps the message content in the same link type as the CTA button. */
function renderMessageLink(cta: TodaysFocusCta, messageEl: ReactNode): ReactNode {
  const { url, external, newTab, openInFancybox } = cta;
  const className = 'todays-focus-card__message-link';

  if (openInFancybox) {
    return (
      <a className={className} data-fancybox data-type="iframe" href={url}>
        {messageEl}
      </a>
    );
  }
  if (external) {
    return (
      <a
        className={className}
        href={url}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
      >
        {messageEl}
      </a>
    );
  }
  if (newTab) {
    const base = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname || ''}` : '';
    const hashPath = (url ?? '').startsWith('/') ? url : `/${url}`;
    return (
      <a className={className} href={`${base}#${hashPath}`} target="_blank" rel="noreferrer">
        {messageEl}
      </a>
    );
  }
  return (
    <Link className={className} to={url}>
      {messageEl}
    </Link>
  );
}

export interface TodaysFocusPanelData {
  isLoading: boolean;
  error: string | null;
  /** Raw `focus.messages` from `getAdvisorDashboardData`. */
  messages: AdvisorDashboardFocusMessage[];
}

interface TodaysFocusCardProps {
  /** Today's Focus content; parent loads advisor dashboard `focus.messages`. */
  focus: TodaysFocusPanelData;
  span?: GlassCardSpan;
  variant?: GlassCardVariant;
  lightTheme?: boolean;
}

export function TodaysFocusCard({ focus, span, variant, lightTheme = false }: TodaysFocusCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const { isLoading, error: errorText, messages } = focus;
  const error = errorText ? new Error(errorText) : null;
  const showDots = messages.length > 1;
  const headerExtra = showDots ? (
    <TodaysFocusCardDots count={messages.length} currentIndex={currentIndex} onSelect={setCurrentIndex} />
  ) : undefined;

  const emptyMessage = (i18n.translate('glassWidgets.todaysfocus.empty') as string) || 'No focus items for today';
  const loadingMessage = (i18n.translate('glassWidgets.todaysfocus.loading') as string) || 'Loading…';
  const errorMessage = (i18n.translate('glassWidgets.todaysfocus.error') as string) || 'Something went wrong';

  const slideElements = messages.map((m, index) => {
    const rawMessage = m.message;
    const filteredMessage = rawMessage.replace(/<p><br><\/p>/gi, '');
    const cta = focusMessageToCta(m);
    const messageEl = <div className="todays-focus-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />;
    return (
      <div key={index} className="todays-focus-card__slide" aria-live="polite">
        {cta !== null ? renderMessageLink(cta, messageEl) : messageEl}
        {cta !== null && <div className="todays-focus-card__buttons">{renderSlideCta(cta, 0)}</div>}
      </div>
    );
  });

  const cardTitle = (i18n.translate('glassWidgets.todaysfocus.title') as string) || "Today's Focus";
  const carouselAria = (i18n.translate('glassWidgets.todaysfocus.carouselAria') as string) || "Today's focus";

  return (
    <GlassCard title={cardTitle} span={span} variant={variant} lightTheme={lightTheme} className="todays-focus-card" headerExtra={headerExtra}>
      {isLoading && <div className="todays-focus-card__loading">{loadingMessage}</div>}

      {!isLoading && error && <div className="todays-focus-card__empty todays-focus-card__error">{errorMessage}</div>}

      {!isLoading && !error && messages.length === 0 && <div className="todays-focus-card__empty">{emptyMessage}</div>}

      {!isLoading && !error && messages.length > 0 && (
        <div className="todays-focus-card__carousel" role="region" aria-label={carouselAria}>
          {/* @ts-expect-error nuka-carousel accepts children; type defs omit it */}
          <Carousel
            autoplay={showDots}
            autoplayInterval={15000}
            wrapAround
            pauseOnHover
            slideIndex={currentIndex}
            afterSlide={setCurrentIndex}
            withoutControls
            renderBottomCenterControls={() => null}
          >
            {slideElements}
          </Carousel>
        </div>
      )}
    </GlassCard>
  );
}
